import { DocumentNode } from 'graphql';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import { GetRegistrationSummaryQuery as GetRegistrationSummary } from '__generated__/graphql';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import SendRegistrationConfirmationMutation from './SendRegistrationConfirmationMutation';
import UI from '../../../../common/UI';
import useMutation from '../../../../api/useMutation';

type Registration = GetRegistrationSummary['registrationSummary']['registrations'][0];

export interface SendConfirmationFormProps {
  registration: Registration;
  onSuccess?: () => void;
  onCancel?: () => void;
  refetchQueries?: DocumentNode[];
}

export const SendConfirmationForm = ({
  registration, onSuccess, onCancel, refetchQueries,
}: SendConfirmationFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();

  const [
    sendRegistrationConfirmation,
    { loading },
  ] = useMutation(
    SendRegistrationConfirmationMutation,
    {
      variables: {
        id: registration.id,
      },
      onCompleted: () => {
        onSuccess?.();
        notifier.success(t('notifications.confirmation_sent'));
      },
      refetchQueries,
      awaitRefetchQueries: true,
    },
  );

  return (
    <UI.Form onSubmit={() => sendRegistrationConfirmation()}>
      <UI.FormGrid>
        <UI.H4>
          {t('send_confirmation')}
        </UI.H4>
        <UI.HR />
        <UI.InputGroup>
          <UI.InputLabel>{t('to')}</UI.InputLabel>
          {registration.participant.email}
        </UI.InputGroup>
        <UI.HR />
        <UI.Div sc={{ textAlign: 'right' }}>
          {onCancel && (
            <UI.Button onClick={() => onCancel()} disabled={loading}>
              {t('cancel')}
            </UI.Button>
          )}
          {' '}
          <UI.Button
            type="submit"
            sc={{ brand: 'secondary' }}
            disabled={loading}
          >
            {t('confirm')}
          </UI.Button>
        </UI.Div>
      </UI.FormGrid>
    </UI.Form>
  );
};

interface UseSendConfirmationModalProps {
  registration: Registration;
  refetchQueries?: DocumentNode[];
}

const useSendConfirmationModal = ({ registration, refetchQueries }: UseSendConfirmationModalProps) => {
  const [openModal, closeModal] = useModal(
    () => (
      <UI.Modal close={closeModal}>
        <SendConfirmationForm
          registration={registration}
          refetchQueries={refetchQueries}
          onSuccess={closeModal}
          onCancel={closeModal}
        />
      </UI.Modal>
    ),
    [registration, refetchQueries],
  );

  return [openModal, closeModal];
};

export default useSendConfirmationModal;
